import React, { useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import styles from './index.module.scss';
import conjureImg from './conjure.png';
import appStoreButton from './apple.svg';
import playStoreButton from './android.svg';
import backArrow from './back-arrow.svg';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import ReCAPTCHA from 'react-google-recaptcha';
import { site_captcha_key } from '../../../constants';


const detectMobileDevice = () => {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);

  // Covers iPhone/iPad/iPod, plus iPadOS reporting as Mac.
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isAndroid) return 'android';
  if (isIOS) return 'ios';
  return null;
};

const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

export default function SignUpPanel({ className, ...rest }) {
  const recaptchaRef = useRef(null);
  const [hasCheckedDeviceType, setHasCheckedDeviceType] = useState(false);
  const [phoneValue, setPhoneValue] = useState();
  const [email, setEmail] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [detectedDeviceType, setDetectedDeviceType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const mergedClassName = [
    styles.signupPanel,
    (showForm || success) && styles.expanded,
    className
  ].filter(Boolean).join(' ');

  useEffect(() => {
    const detected = detectMobileDevice();
    setDetectedDeviceType(detected);
    if (detected) {
      setDeviceType(detected);
    }
    setHasCheckedDeviceType(true);
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setShowForm(false);
        setPhoneValue('');
        setEmail('');
        setError('');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const canSubmit = useMemo(() => {
    const emailOk = isValidEmail(email);
    const deviceOk = Boolean(detectedDeviceType) || deviceType === 'ios' || deviceType === 'android';
    const phoneOk = Boolean(phoneValue) && isValidPhoneNumber(phoneValue);
    return emailOk && deviceOk && phoneOk && !isSubmitting && !success;
  }, [email, deviceType, detectedDeviceType, isSubmitting, phoneValue, success]);

  const handleStoreButtonClick = (device) => {
    setDeviceType(device);
    setShowForm(true);
  };

  const handleBackClick = () => {
    setSuccess(false);
    setShowForm(false);
    setPhoneValue('');
    setEmail('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError('');
    setSuccess(false);
    try {
      let token;
      if (site_captcha_key && recaptchaRef.current && recaptchaRef.current.executeAsync) {
        token = await recaptchaRef.current.executeAsync();
        if (recaptchaRef.current.reset) {
          recaptchaRef.current.reset();
        }
      }

      // Use newsletters service like bf_island_ui
      const subscriptions = deviceType === 'android'
        ? ['buzzfeed_email_conjure_android']
        : ['buzzfeed_email_conjure'];

      const response = await fetch('/newsletters/api/subhub/v1/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: 'buzzfeed',
          subscriptions,
          source: 'conjure-waitlist',
          'g-recaptcha-response': token,
          email: String(email).trim(),
          phone_number: phoneValue,
        }),
      });

      if (!response.ok) {
        let errorBody;
        try {
          errorBody = await response.json();
        } catch (e) {
          // ignore
        }
        // eslint-disable-next-line no-console
        console.error('[Conjure] Waitlist signup failed', response.status, errorBody);
        setError(errorBody?.error || 'Uh-oh! Something went wrong. Please try again.');
        return;
      }

      setSuccess(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Conjure] Waitlist signup request errored', error);
      setError('Uh-oh! Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={mergedClassName} {...rest}>
      <Head>
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='' />
        {/* eslint-disable-next-line @next/next/no-page-custom-font */}
        <link
          href='https://fonts.googleapis.com/css2?family=Saira:wght@400;700&display=swap'
          rel='stylesheet'
        />
      </Head>

      <img className={styles.conjureImage} src={conjureImg} alt='Conjure' />
      <div className={styles.waitlistTagline}>
        <div className={styles.waitlistTaglineLine}>What is reality hiding from you?</div>
      </div>

      {showForm && (
        <div className={styles.inputsSection}>
          {site_captcha_key && (
            <ReCAPTCHA
              ref={recaptchaRef}
              size='invisible'
              sitekey={site_captcha_key}
              style={{ visibility: 'hidden' }}
            />
          )}

          {hasCheckedDeviceType && !detectedDeviceType && (
            <select
              className={styles.deviceSelect}
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              aria-label='Device type'
              required
            >
              <option value='' disabled>
                select device
              </option>
              <option value='ios'>iOS</option>
              <option value='android'>Android</option>
            </select>
          )}

          <input
            className={styles.emailInput}
            type='email'
            inputMode='email'
            autoComplete='email'
            placeholder='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className={styles.phoneRow}>
            <PhoneInput
              className={styles.phoneInput}
              defaultCountry='US'
              flags={flags}
              value={phoneValue}
              onChange={(value) => {
                setPhoneValue(value);
                if (error && String(error).toLowerCase().includes('phone')) {
                  setError('');
                }
              }}
              placeholder='phone number'
              autoComplete='tel'
              countryCallingCodeEditable={false}
            />
          </div>
        </div>
      )}

      {success ? (
        <button type='button' className={styles.successButton} disabled>
          WAITLIST JOINED!
        </button>
      ) : showForm ? (
        <>
          <button type='button' className={styles.signUpButton} onClick={handleSubmit} disabled={!canSubmit}>
            JOIN THE WAITLIST
          </button>
          <button type='button' className={styles.backButtonContainer} onClick={handleBackClick} aria-label='Go back'>
            <img src={backArrow} alt='Back' />
            <span className={styles.backButtonText}>BACK TO APP DOWNLOAD</span>
          </button>
        </>
      ) : (
        <div className={[styles.storeButtonsContainer, showForm && styles.fadeOut].filter(Boolean).join(' ')}>
          <a href='https://apps.apple.com/us/app/conjure-camera/id6754414390' target='_blank' rel='noopener noreferrer'>
            <img src={appStoreButton} alt='Download on App Store' className={styles.storeButton} />
          </a>
          <img src={playStoreButton} alt='Get it on Google Play' className={styles.storeButton} onClick={() => handleStoreButtonClick('android')} />
        </div>
      )}
    </div>
  );
}